import * as React from "react";
import {
  type ColumnDef,
  type FilterFn,
  type FilterMeta,
  type Row,
  type TableOptions,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { rankItem } from "@tanstack/match-sorter-utils";
import {
  TableCaption,
  TableWrapper,
  TableRoot,
  TableHeader,
  TableRow,
  TableBody,
  TableCell,
} from "@sproutsocial/seeds-react-table/v2";
import type {
  TypeDataTableProps,
  TypeDataTableSortingState,
  TypeRenderableCellValue,
} from "./DataTableTypes";
import { DataTableHeaderCell } from "./DataTableHeaderCell";
import { SimplePagination } from "./SimplePagination";
import { Pagination } from "@sproutsocial/seeds-react-pagination";
import styled from "styled-components";

const FixedTableRoot = styled(TableRoot)`
  table-layout: fixed;
`;

// Fuzzy filter function for searching across all columns
const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value);
  addMeta({ itemRank });
  return itemRank.passed;
};

// Default renderCell: convert value to string
const defaultRenderCell = <
  TData extends Record<string, TypeRenderableCellValue>
>(
  value: TData[keyof TData]
) => String(value ?? "");

// Helper function to filter undefined values from a partial options object
const filterUndefined = <T extends Record<string, unknown>>(
  obj: T
): Partial<T> => {
  const result: Partial<T> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      result[key as keyof T] = value as T[keyof T];
    }
  }
  return result;
};

/**
 * DataTable component with TanStack Table integration
 *
 * Features:
 * - String array columns with automatic camelCase mapping
 * - TanStack ColumnDef[] for typed data with full control
 * - Sorting functionality
 * - Custom cell and header renderers
 * - Accessible with required caption
 * - Props to customize child components (tableCellProps, tableRowProps, etc.)
 *
 * For simple tables with string/ReactNode arrays, use TableV2 directly.
 * For large datasets requiring virtualization, use DataTableVirtualized.
 *
 * @example
 * Basic usage:
 * ```tsx
 * <DataTable
 *   columns={["Name", "Age", "Email"]}
 *   data={users}
 *   caption="Users"
 *   emptyMessage="No users found"
 * />
 * ```
 *
 * @example
 * Controlled sorting:
 * ```tsx
 * import { DataTableSortingState } from "@sproutsocial/seeds-react-data-table";
 *
 * const [sorting, setSorting] = useState<DataTableSortingState>([
 *   { id: "name", desc: false },
 * ]);
 *
 * <DataTable
 *   columns={columns}
 *   data={users}
 *   caption="Users"
 *   emptyMessage="No users found"
 *   state={{ sorting }}
 *   onSortingChange={setSorting}
 * />
 * ```
 *
 *
 * @example
 * Custom child component styling:
 * ```tsx
 * import styled from "styled-components";
 * import { TableRow } from "@sproutsocial/seeds-react-table/v2";
 *
 * const StyledRow = styled(TableRow)`
 *   &:hover { background-color: #f3f4f6; }
 * `;
 *
 * <DataTable
 *   columns={columns}
 *   data={users}
 *   caption="Users"
 *   emptyMessage="No users found"
 *   tableRowProps={{ as: StyledRow }}
 * />
 * ```
 */
export const DataTable = <
  TData extends Record<string, TypeRenderableCellValue>
>({
  columns,
  data,
  caption,
  displayCaption,
  emptyMessage,
  renderCaption,
  tableCellProps,
  tableRowProps,
  tableBodyProps,
  tableHeaderProps,
  tableHeaderCellProps,
  state,
  onSortingChange,
  onPageIndexChange,
  onPageSizeChange,
  onColumnVisibilityChange,
  onRowSelectionChange,
  getRowId,
  enableSorting = true,
  serverSideSorting,
  simplePaginationConfig,
  paginationConfig,
  fixedLayout,
  _experimentalSearch,
  _experimentalSearchFilterFn,
}: TypeDataTableProps<TData>) => {
  const renderedCaption = renderCaption ? renderCaption(caption) : caption;

  // Internal state for uncontrolled sorting
  const [internalSorting, setInternalSorting] =
    React.useState<TypeDataTableSortingState>([]);

  // Determine pagination type
  const hasSimplePagination = Boolean(simplePaginationConfig);
  const hasComplexPagination = Boolean(paginationConfig);
  const hasPagination = hasSimplePagination || hasComplexPagination;

  // Set initial page size based on pagination type
  const initialPageSize = hasComplexPagination
    ? paginationConfig!.initialPageSize
    : hasSimplePagination
    ? simplePaginationConfig!.initialVisibleRows
    : -1;

  // Pagination state for TanStack Table
  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: initialPageSize,
  });

  // Reset pagination to page 1 when search changes
  React.useEffect(() => {
    if (_experimentalSearch && hasPagination) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [_experimentalSearch, hasPagination]);

  // Use controlled state if provided, otherwise use internal state
  const sortingState = state?.sorting ?? internalSorting;
  const handleSortingChange = onSortingChange ?? setInternalSorting;

  // Convert DataTableColumn to ColumnDef
  const columnDefs = React.useMemo<ColumnDef<TData, unknown>[]>(
    () =>
      columns.reduce<ColumnDef<TData, unknown>[]>(
        (
          acc,
          {
            key,
            label,
            renderCell,
            sortingFn,
            disableGlobalFilter,
            disableSorting,
            sortDescFirst,
            size,
          }
        ) => [
          ...acc,
          {
            accessorKey: key,
            header: typeof label === "number" ? String(label) : label,
            cell: (info) =>
              (renderCell ?? defaultRenderCell)(
                info.getValue() as TData[keyof TData],
                info.row.original
              ),
            // omits sortingFn if not provided since it causes errors when set to undefined
            ...(sortingFn && {
              sortingFn: (rowA, rowB, columnId) => {
                const cellValueA = rowA.original[columnId as keyof TData];
                const cellValueB = rowB.original[columnId as keyof TData];
                return sortingFn(cellValueA, cellValueB, {
                  rowA: rowA.original,
                  rowB: rowB.original,
                  columnId,
                });
              },
            }),
            size,
            // Pass inverse of disableGlobalFilter to TanStack's enableGlobalFilter
            ...(disableGlobalFilter === true && {
              enableGlobalFilter: false,
            }),
            // Pass inverse of disableSorting to TanStack's enableSorting
            ...(disableSorting === true && {
              enableSorting: false,
            }),
            ...(sortDescFirst !== undefined && {
              sortDescFirst: sortDescFirst,
            }),
          } as ColumnDef<TData, unknown>,
        ],
        []
      ),
    [columns]
  );

  // TanStack Table is sensitive to undefined props and will throw errors if optional
  // callbacks/flags are explicitly set to undefined. We filter them out and memoize to
  // prevent errors and unnecessary re-renders.
  const tableOptions = React.useMemo((): TableOptions<TData> => {
    const paginationOptions = {
      ...pagination,
      ...state?.pagination,
    };
    const options: Partial<TableOptions<TData>> = {
      data,
      columns: columnDefs,
      state: {
        ...state,
        sorting: sortingState,
        pagination: hasPagination ? paginationOptions : undefined,
        globalFilter: _experimentalSearch,
      },
      onSortingChange: handleSortingChange,
      onColumnVisibilityChange,
      onRowSelectionChange,
      getRowId: getRowId
        ? (originalRow: TData, index: number) => getRowId(originalRow, index)
        : undefined,
      enableSorting,
      manualSorting: serverSideSorting,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: _experimentalSearch
        ? getFilteredRowModel()
        : undefined,
      getPaginationRowModel:
        hasPagination && !state?.pagination
          ? getPaginationRowModel()
          : undefined,
      manualPagination: state?.pagination ? true : undefined,
      onPaginationChange: hasPagination ? setPagination : undefined,
      getSortedRowModel:
        serverSideSorting !== true ? getSortedRowModel() : undefined,
      filterFns: {
        fuzzy: fuzzyFilter,
      },
      // When a custom filterFn is passed through, use it during global filtering
      // instead of the default fuzzyFilter. This allows columns to opt into
      // searching across multiple fields (e.g. name + email in one cell).
      globalFilterFn: (typeof _experimentalSearchFilterFn === "function"
        ? (row: Row<TData>, columnId: string, filterValue: string) =>
            _experimentalSearchFilterFn(row.original, columnId, filterValue)
        : _experimentalSearchFilterFn ?? "fuzzy") as any,
    };

    // Filter out undefined values to prevent TanStack Table errors
    return filterUndefined(options) as TableOptions<TData>;
  }, [
    data,
    columnDefs,
    state,
    sortingState,
    handleSortingChange,
    onColumnVisibilityChange,
    onRowSelectionChange,
    getRowId,
    enableSorting,
    serverSideSorting,
    pagination,
    hasPagination,
    _experimentalSearch,
    _experimentalSearchFilterFn,
  ]);

  const table = useReactTable<TData>(tableOptions);

  const rows = table.getRowModel().rows;
  const columnCount = table.getAllLeafColumns().length;
  const filteredRowCount = table.getFilteredRowModel().rows.length;

  // Pagination handlers
  const handleChangePageSize = (newPageSize: number) => {
    table.setPageSize(newPageSize);
  };

  const handlePaginationChange = React.useCallback(
    (pageIndex: number) => {
      onPageIndexChange?.(pageIndex);
      if (!state?.pagination) {
        table.setPageIndex(pageIndex);
      }
    },
    [paginationConfig, table]
  );

  const handlePageSizeChange = React.useCallback(
    (pageSize: number) => {
      onPageSizeChange?.(pageSize);
      if (!state?.pagination) {
        table.setPageSize(pageSize);
      }
    },
    [state, table]
  );

  const TableComponent = fixedLayout ? FixedTableRoot : TableRoot;

  return (
    <>
      <TableWrapper>
        <TableComponent>
          <TableCaption isVisible={displayCaption ?? true}>
            {renderedCaption as React.ReactNode}
          </TableCaption>
          <TableHeader {...tableHeaderProps}>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((header) => {
                  const col = columns.find((c) => c.key === header.column.id);
                  return (
                    <DataTableHeaderCell
                      key={header.id}
                      header={header}
                      {...tableHeaderCellProps}
                      style={col?.size ? { width: `${col.size}px` } : undefined}
                    />
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody {...tableBodyProps}>
            {rows.length === 0 ? (
              <TableRow {...tableRowProps}>
                <TableCell id="no-data" colSpan={columnCount}>
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id} {...tableRowProps}>
                  {row.getVisibleCells().map((cell) => {
                    const columnProp = columns.find(
                      (col) => col.key === cell.column.id
                    );
                    const isRowHeader = columnProp?.isRowHeader === true;

                    return (
                      <TableCell
                        {...tableCellProps}
                        key={cell.id}
                        id={cell.id}
                        as={isRowHeader ? "th" : tableCellProps?.as}
                        scope={isRowHeader ? "row" : undefined}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </TableComponent>
      </TableWrapper>
      {simplePaginationConfig && (
        <SimplePagination
          simplePaginationConfig={simplePaginationConfig}
          onChangePageSize={handleChangePageSize}
          rowsLength={rows.length}
          dataLength={filteredRowCount}
        />
      )}
      {paginationConfig && (
        <Pagination
          paginationConfig={paginationConfig}
          currentPageIndex={
            state?.pagination?.pageIndex ?? pagination.pageIndex
          }
          pageSize={state?.pagination?.pageSize ?? pagination.pageSize}
          totalRows={state?.pagination?.totalRows ?? filteredRowCount}
          onPageChange={handlePaginationChange}
          onPageSizeChange={handlePageSizeChange}
        />
      )}
    </>
  );
};

export default DataTable;
